import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AuthUser } from '@/types';

const TOKEN_KEY = 'md_token';
const USER_KEY = 'md_user';

interface AuthContextValue {
    user: AuthUser | null;
    token: string | null;
    isLoggedIn: boolean;
    login: (token: string, user: AuthUser) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStorage(): { token: string | null; user: AuthUser | null } {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const raw = localStorage.getItem(USER_KEY);
        const user = raw ? (JSON.parse(raw) as AuthUser) : null;
        return { token, user };
    } catch {
        return { token: null, user: null };
    }
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const stored = readStorage();
    const [token, setToken] = useState<string | null>(stored.token);
    const [user, setUser] = useState<AuthUser | null>(stored.user);

    const login = useCallback((newToken: string, newUser: AuthUser) => {
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(USER_KEY, JSON.stringify(newUser));
        setToken(newToken);
        setUser(newUser);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, token, isLoggedIn: !!token && !!user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
